/**
 * Utility functions for generating video thumbnails using Canvas API
 * Web app approach: Generate on-demand with fallback for CORS issues
 */

/**
 * Generate a thumbnail from a video URL using Canvas API
 * Handles CORS restrictions by returning null if canvas is tainted
 * @param {string} videoUrl - The URL of the video
 * @param {number} timestamp - Time in seconds to capture frame (default: 1 second)
 * @returns {Promise<string|null>} Base64 data URL of the thumbnail or null
 */
export const generateVideoThumbnail = (videoUrl, timestamp = 1) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.setAttribute('crossOrigin', 'anonymous');
    video.preload = 'auto';
    video.muted = true;
    video.src = videoUrl;

    let timeoutId;
    let resolved = false;

    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.pause();
      video.src = '';
      if (timeoutId) clearTimeout(timeoutId);
    };

    const doResolve = (value) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(value);
      }
    };

    const onLoadedMetadata = () => {
      try {
        // Set current time to get a frame
        video.currentTime = Math.min(timestamp, Math.max(0.1, video.duration || 1));
      } catch (err) {
        console.error('Error setting video time:', err);
        doResolve(null);
      }
    };

    const onSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx || video.videoWidth === 0 || video.videoHeight === 0) {
          console.warn('Cannot get canvas context or video dimensions');
          doResolve(null);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        try {
          // Draw the frame
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Test if we can read the canvas (CORS check)
          try {
            ctx.getImageData(0, 0, 1, 1);
            // Canvas is accessible, convert to data URL
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
            doResolve(thumbnailUrl);
          } catch (corsError) {
            // Canvas is tainted by CORS
            doResolve(null);
          }
        } catch (drawError) {
          console.error('Error drawing video frame:', drawError);
          doResolve(null);
        }
      } catch (error) {
        console.error('Error in seeked handler:', error);
        doResolve(null);
      }
    };

    const onError = (e) => {
      doResolve(null);
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    // Set timeout
    timeoutId = setTimeout(() => {
      doResolve(null);
    }, 8000);
  });
};

/**
 * Generate thumbnail with timeout (matches mobile's 6-second timeout pattern)
 * @param {string} videoUrl - The URL of the video
 * @param {number} timeoutMs - Timeout in milliseconds (default: 6000)
 * @param {number} timestamp - Time in seconds to capture frame (default: 1 second)
 * @returns {Promise<string|null>} Base64 data URL of the thumbnail or null
 */
export const generateVideoThumbnailWithTimeout = (videoUrl, timeoutMs = 6000, timestamp = 1) => {
  return Promise.race([
    generateVideoThumbnail(videoUrl, timestamp),
    new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
};
